import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Table } from 'reactstrap';
import { deletePartnerLogo, getLogosSettings, setPartnerLogo } from '../../actions/ActionPartnerWL';
import imgfLoading from '../../assets/img/preloader-black.gif';
import Alert from "../../components/Alert";
import SweetAlert from 'react-bootstrap-sweetalert';
import { createPortal } from 'react-dom';

const LogoList = React.forwardRef(({ nWlpnId, cBaseLogoUrl, setAlert }, ref) => {
  const [bIsLoading, setIsLoading] = useState(true);
  const [aLogos, setLogos] = useState([]);

  const [cModalType, setModalType] = useState('');
  const [oSelectedLogo, setSelectedLogo] = useState(null);

  const loadSystemLogos = async () => {
    setIsLoading(true);
    try {
      const aResults = await getLogosSettings();
      setLogos(aResults);
    } catch (oError) {
      setAlert('error', 'Falha ao carregar logos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSystemLogos();
  }, []);

  const openModal = (oLogo, cType) => {
    setSelectedLogo(oLogo);
    setModalType(cType);
  };

  const closeModal = () => {
    setSelectedLogo(null);
    setModalType('');
  };

  const onSubmitLogo = async (fdLogoData) => {
    await setPartnerLogo(nWlpnId, fdLogoData);
    setAlert('success', 'Logo atualizada com sucesso!');
    closeModal();
  };

  const onDeleteLogo = async (oLogo) => {
    await deletePartnerLogo(nWlpnId, `${oLogo.logo_cname}.${oLogo.logo_cext}`);
    setAlert('success', 'Logo excluída com sucesso!');
  };

  return (
    <div className="basic-config" style={{marginBottom: '40px' }} ref={ref}>
      <div className="title-button" style={{marginBottom: '10px'}}>
        <p style={{ fontSize: '1.8rem' }}>Logos</p>
      </div>

      {!!oSelectedLogo && cModalType === 'edit' && (
        <ModalEditLogo
          oLogo={oSelectedLogo}
          cBaseLogoUrl={cBaseLogoUrl}
          onSubmit={(fdLogoData) => onSubmitLogo(fdLogoData)}
          onClose={closeModal}
        />
      )}

      <Table hover responsive striped>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Dimensões</th>
            <th>Em uso</th>
            <th>Ação</th>
          </tr>
        </thead>
        <tbody>
          {bIsLoading ? (
            <tr>
              <td colSpan="4" className="centered-text">
                <img alt='Carregando' src={imgfLoading} /> Carregando...
              </td>
            </tr>
          ) : aLogos.length === 0 ? (
            <tr>
              <td colSpan="4" className="centered-text">
                Nenhuma logo cadastrada no sistema
              </td>
            </tr>
          ) : aLogos.map((oLogo) => (
            <LogoListItem
              key={oLogo.logo_nid}
              oLogo={oLogo}
              onEdit={(oLogo) => openModal(oLogo, 'edit')}
              onDelete={(oLogo) => openModal(oLogo, 'delete')}
            />
          ))}
        </tbody>
      </Table>
    </div>
  );
});

const LogoListItem = ({ oLogo, onEdit = () => {}, onDelete = () => {} }) => {
  const cFullName = useMemo(() => (oLogo.logo_cfilename + '.' + oLogo.logo_cext), [oLogo]);
  const cDimension = useMemo(() => (`${oLogo.logo_nwidth}x${oLogo.logo_nheight}px`), [oLogo]);

  return (
    <tr>
      <td>{cFullName}</td>
      <td>{cDimension}</td>
      <td>{oLogo.logo_bisactive ? 'Sim' : 'Não'}</td>
      <td>
        <a
          className="edit-btn"
          title="Editar"
          onClick={() => onEdit(oLogo)}
        >
          <i className="icon-edit" />
        </a>
      </td>
    </tr>
  );
};

const ModalEditLogo = ({ oLogo, cBaseLogoUrl, onSubmit = async () => {}, onClose = () => {} }) => {
  const cFullName = useMemo(() => `${oLogo.logo_cfilename}.${oLogo.logo_cext}`, [oLogo]);
  const cLogoUrl = useMemo(() => `${cBaseLogoUrl}${cFullName}`, [cBaseLogoUrl, cFullName]);
  const oFileInputRef = useRef(null);

  const [bIsSubmitting, setIsSubmitting] = useState(false);
  const [cErrorMsg, setErrorMsg] = useState('');
  const [fNewFile, setNewFile] = useState(null);
  const [cPreviewUrl, setPreviewUrl] = useState(cLogoUrl);

  const fileChangedHandler = useCallback((evt) => {
    evt.preventDefault();
    const fFile = evt.target.files[0];
    if (!fFile) return;
    if (fFile.size > 1024 * 1024) {
      setErrorMsg('Imagem deve ter no máximo 1MB');
      return;
    }

    const frChanged = new FileReader();
    frChanged.onloadend = () => {
      setPreviewUrl(frChanged.result);
      setNewFile(fFile);
    };
    frChanged.readAsDataURL(fFile);
  }, []);

  const triggerInputFile = useCallback(() => {
    if (oFileInputRef.current) {
      oFileInputRef.current.click();
    }
  }, [oFileInputRef]);

  const clearImage = useCallback((evtClick) => {
    evtClick.preventDefault();
    setNewFile(null);
    setPreviewUrl(cLogoUrl);
    oFileInputRef.current.value = '';
  }, [cLogoUrl, oFileInputRef]);

  const handleSubmit = useCallback(async (evtSubmit) => {
    evtSubmit.preventDefault();
    evtSubmit.stopPropagation();
    if (bIsSubmitting) return;
    setIsSubmitting(true);

    const fdData = new FormData();
    fdData.append('logo_file', fNewFile);
    fdData.append('logo_name', oLogo.logo_cfilename);
    try {
      await onSubmit(fdData);
      onClose();
    } catch (oError) {
      console.log(oError.aMsgErrors);
      setIsSubmitting(false);
    }
  })

  return createPortal((
      <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
        <div className="head-modal">
          Editar Logo
          <span role="button" tabIndex="0" onClick={onClose} className="close" />
        </div>
        <form className="form" onSubmit={handleSubmit}>
          {cErrorMsg && <Alert type="error" isOpen onCloseAlert={() => setErrorMsg('')}>{cErrorMsg}</Alert>}
          <label className="split1 colspan">
            <input
              onChange={fileChangedHandler}
              type="file"
              name="logo_file"
              className="hidden"
              accept={"." + oLogo.logo_cext}
              ref={oFileInputRef}
              required
            />
            <p onClick={(evt) => evt.preventDefault()}>{cFullName}</p>
            <img width={Math.min(oLogo.logo_nwidth, 640)} height={Math.min(oLogo.logo_nheight, 640)} src={cPreviewUrl} id="avatar-preview" />
            <button
              type="button"
              onClick={triggerInputFile}
              className="btn alternate back small-bt small btn-outline-secondary"
            >
              Carregar imagem
            </button>
          </label>
          {cLogoUrl !== cPreviewUrl && (
            <label className="split">
              <button type="button" onClick={clearImage} className="btn alternate back btn btn-outline-secondary">
                Limpar seleção
              </button>
            </label>
          )}

          <div className="button-stack">
            <button
              type="button"
              className="btn alternate back btn btn-outline-secondary"
              title="Cancelar"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" value="salvar" className="btn btn btn-secondary" title="Salvar" disabled={bIsSubmitting || cPreviewUrl === cLogoUrl}>
              Salvar
            </button>
          </div>
        </form>
      </SweetAlert>
    ),
    document.body);
}

export default LogoList;